import { emailRegex, phoneRegex, ROUTE_PATHS } from '@/constants';
import type { IBaseForm, IUser } from '@/types';
import { hasEmptyFields, hasFormAnyError } from '@/utils';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Button,
  Flex,
  Heading,
  Link,
  useControllableState,
} from '@chakra-ui/react';
import type { SubmitHandler } from 'react-hook-form';
import { FormProvider, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { ERRORS } from '../../constants';
import * as Form from '../shared';
import { AddressFields } from '../shared';
import { useUserApi, useValidator } from '@/features/users';

export const UserForm: React.FC<IBaseForm<IUser>> = ({
  defaultValues,
  onSubmit: providedOnSubmit,
}): JSX.Element => {
  const { t } = useTranslation();
  const { validateFiscalCode } = useValidator();
  const methods = useForm<IUser>({ mode: 'onBlur', defaultValues });
  const userApi = useUserApi();
  const [isMailUsed, setIsMailUsed] = useControllableState({ defaultValue: false });

  const {
    formState: { errors },
    getValues,
    handleSubmit: submitHandler,
    register,
    watch,
  } = methods;

  const formFields = watch([
    'firstName',
    'lastName',
    'city',
    'countryCode',
    'email',
    'phone',
    'streetAddress',
    'countryCode',
    'province',
  ]);

  const areRequiredFieldsEmpty = hasEmptyFields(formFields);
  const isDisabled = areRequiredFieldsEmpty || isMailUsed || hasFormAnyError(errors);

  const handleSubmit: SubmitHandler<IUser> = (data, event): void => {
    event?.preventDefault();

    const updatedUserData = { ...data, customerType: 'PERSON' };

    providedOnSubmit?.(updatedUserData);
  };

  const countryCodeValue = getValues('countryCode') && getValues('countryCode').toLowerCase();

  const fiscalCodeProps = register('fiscalCode', {
    validate: (value) => validateFiscalCode({ countryCode: countryCodeValue, fiscalCode: value }),
  });

  return (
    <FormProvider {...methods}>
      {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
      <form autoComplete="off" id="userForm" name="userForm" onSubmit={submitHandler(handleSubmit)}>
        <Flex direction="column" gap={12} mb={8}>
          <Form.MbeInput
            error={errors.firstName}
            id="firstName"
            placeholder={t('FORM.FIRSTNAME')}
            {...register('firstName', {
              required: { value: true, message: t(ERRORS.empty) },
            })}
          />
          <Form.MbeInput
            error={errors.lastName}
            id="lastName"
            placeholder={t('FORM.LASTNAME')}
            {...register('lastName', {
              required: { value: true, message: t(ERRORS.empty) },
            })}
          />
          <Form.MbeInput
            error={errors.fiscalCode}
            id="fiscalCode"
            placeholder={t('FORM.FISCALCODE')}
            {...fiscalCodeProps}
          />
          <Form.MbeInput
            error={errors.email}
            id="email"
            placeholder={t('FORM.EMAIL')}
            {...register('email', {
              required: { value: true, message: t(ERRORS.empty) },
              pattern: { value: emailRegex, message: t(ERRORS.email) },
              // eslint-disable-next-line @typescript-eslint/no-misused-promises
              onBlur: async (): Promise<void> => {
                const isMailKnown: boolean = await userApi.checkUserEmail(getValues('email'));
                setIsMailUsed(isMailKnown);
              },
            })}
          />
          {isMailUsed && (
            <Alert my={-8} status="error" w="100%">
              <AlertIcon />
              <AlertDescription>
                <Trans
                  components={{
                    url: <Link color="brand.red.light" href={ROUTE_PATHS.userLogin} />,
                  }}
                  i18nKey="ERRORS.ALREADY_USED_EMAIL"
                />
              </AlertDescription>
            </Alert>
          )}
          <Form.MbeInput
            error={errors.phone}
            id="phone"
            placeholder={t('FORM.PHONE')}
            {...register('phone', {
              required: { value: true, message: t(ERRORS.empty) },
              pattern: { value: phoneRegex, message: t(ERRORS.phoneNumber) },
            })}
          />
          <Flex direction="column">
            <Heading fontSize="large" fontWeight="bold" mb={4}>
              {t('WIZARD.USER.ADDRESS_FIELDS_TITLE')}
            </Heading>
            <Flex direction="column" gap={12}>
              <AddressFields />
            </Flex>
          </Flex>
        </Flex>
        <Flex flexDirection="row-reverse" gap={2} justifyContent="space-between">
          <Button disabled={isDisabled} type="submit" variant="mbePrimary">
            {t('ACTIONS.NEXT')}
          </Button>
        </Flex>
      </form>
    </FormProvider>
  );
};
